import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Skeleton, styled } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { FormContainer, TextareaAutosizeElement, TextFieldElement } from 'react-hook-form-mui';

import type { FormFields } from '@/components/Patient/forms/MedicalSummary/MedicalSummary.schema';
import { schema } from '@/components/Patient/forms/MedicalSummary/MedicalSummary.schema';
import { FF_PATIENT_DETAILS_SKELETON_ENABLED } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import { NumberTextField } from '@montugroup/design-system';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';

const StyledBox = styled(Box)({
  width: '100%',
  marginTop: 15,
  marginBottom: 15,
  display: 'flex',
  flexDirection: 'column',
});

type MedicalSummaryProps = {
  defaultValues?: FormFields;
  editable?: boolean;
  onSubmit?: (data: FormFields) => void;
  isLoading?: boolean;
};

export const MedicalSummary = ({ defaultValues, editable, onSubmit, isLoading }: MedicalSummaryProps) => {
  const FORM_NAME: GetPatientDetailsInclude = PATIENT_DETAILS_FORM_NAMES.PATIENT_MEDICAL_INFO;

  const formContext = useForm<FormFields>({
    defaultValues,
    values: defaultValues,
    resolver: zodResolver(schema),
  });

  const { flags } = useFeatureFlags();
  const isSkeletonEnabled = flags[FF_PATIENT_DETAILS_SKELETON_ENABLED];

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';

  const handleOnSubmit = (formData: FormFields) => {
    const isDirty = formContext.formState.isDirty;

    if (onSubmit && isDirty) {
      onSubmit(formData);
    }
  };

  return (
    <FormSection
      title="Medical Summary"
      formName={FORM_NAME}
      formContext={formContext}
      onSubmit={handleOnSubmit}
      noBorder
    >
      <Box data-testid="medical-summary" sx={{ marginTop: 5 }}>
        {isLoading && isSkeletonEnabled ? (
          Array.from({ length: 7 }).map((_, index) => (
            <StyledBox key={index}>
              <Skeleton variant="rounded" height={55} />
            </StyledBox>
          ))
        ) : (
          <FormContainer formContext={formContext}>
            <StyledBox>
              <TextareaAutosizeElement
                label="Presenting Complaint"
                name="conditions"
                resizeStyle="vertical"
                disabled={!isEffectivelyEditable}
              />
            </StyledBox>
            <StyledBox>
              <TextareaAutosizeElement label="Symptoms" name="symptoms" resizeStyle="vertical" disabled={!editable} />
            </StyledBox>
            <StyledBox>
              <TextareaAutosizeElement
                label="Trialled Medication"
                name="medications"
                resizeStyle="vertical"
                disabled={!isEffectivelyEditable}
              />
            </StyledBox>
            <StyledBox>
              <TextareaAutosizeElement
                label="Current Medication List"
                name="currentMedications"
                resizeStyle="vertical"
                disabled={!isEffectivelyEditable}
              />
            </StyledBox>
            <StyledBox>
              <TextareaAutosizeElement
                label="Other Medical History"
                name="comments"
                resizeStyle="vertical"
                disabled={!isEffectivelyEditable}
              />
            </StyledBox>
            <StyledBox>
              <Controller
                name="alcoholPerWeek"
                control={formContext.control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <NumberTextField
                      label="ETOH/Week"
                      fullWidth
                      disabled={!isEffectivelyEditable}
                      value={value}
                      error={!!formContext.formState?.errors?.alcoholPerWeek}
                      helperText={formContext.formState?.errors?.alcoholPerWeek?.message?.toString() ?? ''}
                      onValueChange={(v: { value: string }) => onChange(Number(v.value))}
                    />
                  );
                }}
              />
            </StyledBox>
            <StyledBox>
              <TextFieldElement label="Smoking Hx" name="smoking" disabled={!isEffectivelyEditable} />
            </StyledBox>
          </FormContainer>
        )}
      </Box>
    </FormSection>
  );
};
